import * as React from 'react';
import { cloneElement, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Datagrid,
  FunctionField,
  TextField,
  ReferenceField,
  BooleanField,
  Filter,
  SearchInput,
  Button,
  EditButton,
  DeleteButton,

  useListContext,
  useRefresh,
  useNotify,
  TopToolbar,
  CreateButton,
  sanitizeListRestProps,
  usePermissions,
} from 'react-admin';

import ButtonGroup from '@material-ui/core/ButtonGroup';
import PreviewIcon from '@material-ui/icons/Visibility';
import PublishIcon from '@material-ui/icons/Publish';
import RelativeImageField from '../shared/RelativeImageField';
import PublishDateField from '../shared/PublishDateField';
import hasPermission from '../shared/hasPermission';
import isPublished from '../shared/isPublished';
import hasUnpublishedChanges from '../shared/hasUnpublishedChanges';
import { sanitizeButtonProps } from '../shared/sanitizeReactProps';
import List from '../shared/SimappList';

const API_URL = process.env.REACT_APP_API_URL;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContentPieceActions = props => {
  const {
    className,
    exporter,
    filters,
    maxResults,
    ...rest
  } = props;
  const {
    currentSort,
    resource,
    displayedFilters,
    filterValues,
    hasCreate,
    basePath,
    selectedIds,
    showFilter,
    total,
  } = useListContext();
  const { permissions } = usePermissions();

  return hasPermission(permissions, { create: ['/api/web/content_pieces'] })
    ? (
      <TopToolbar
        className={className}
        {...sanitizeListRestProps(rest)}
      >
        {filters && cloneElement(filters, {
          resource,
          showFilter,
          displayedFilters,
          filterValues,
          context: 'button',
        })}
        <CreateButton basePath={basePath} />
      </TopToolbar>
    ) : null;
};

const ContentPieceFilter = props => (
  <Filter {...props}>
    <SearchInput source="q" alwaysOn />
  </Filter>
);

const PreviewButton = ({ record }) => (
  <Button
    variant="outlined"
    color="primary"
    href={`${API_URL}/preview/${record.template_id}/${record.id}`}
    target={`content-piece-preview-${record.id}`}
    title="Preview"
  >
    <PreviewIcon />
  </Button>
);

const PublishButton = props => {
  const refresh = useRefresh();
  const notify = useNotify();
  const publishContentPiece = () => fetch(`${API_URL}/publish/content_piece/${props.record.id}`, {
    credentials: 'include',
    mode: 'cors',
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json'
    }
  })
    .then(res => {
      if (res.ok) {
        refresh();
        notify('Content Piece published', 'success');
      } else {
        throw new Error('Invalid response.');
      }
    })
    .catch(err => {
      notify(err.toString(), 'warning');
    });
  return ((props.record.active && !isPublished(props.record)) || hasUnpublishedChanges(props.record))
    ? (
      <Button
        variant="outlined"
        color="primary"
        onClick={publishContentPiece}
        title="Publish"
      >
        <PublishIcon />
      </Button>
    ) : null;
};

const ContentPieceList = props => {
  const { permissions } = usePermissions();

  return (
    <List
      {...props}
      title="Content Pieces"
      filters={<ContentPieceFilter />}
      actions={<ContentPieceActions />}
      sort={{ order: 'DESC', field: 'id' }}
    >
      <Datagrid>
        <TextField source="id" label="ID" />
        <RelativeImageField
          source="thumbnail.url"
          label="Thumbnail"
          title="fileInfo.name"
          imageBasePath={BACKEND_URL}
        />
        <TextField source="mastercontrol_id" label="Master Control ID" />
        <TextField source="title" />
        <PublishDateField
          source="released_at"
          label="Published at"
          showTime
        />
        <FunctionField
          label="Actions"
          textAlign="right"
          render={record => (
            <ButtonGroup>
              {hasPermission(permissions, { read: ['/api/web/preview/'] })
            && (
              <PreviewButton
                {...sanitizeButtonProps(props)}
                record={record}
              />
            )}
              {hasPermission(permissions, { update: ['/api/web/content_pieces/'] })
            && (
              <EditButton
                {...sanitizeButtonProps(props)}
                title="Edit"
                label={null}
                record={record}
              />
            )}
              {hasPermission(permissions, { delete: ['/api/web/content_pieces/'] })
            && (
              <DeleteButton
                {...sanitizeButtonProps(props)}
                title="Delete"
                undoable={false}
                label={null}
                record={record}
              />
            )}
              {hasPermission(permissions, { update: ['/api/web/publish/content_piece/'] })
            && (
              <PublishButton
                {...sanitizeButtonProps(props)}
                record={record}
              />
            )}
            </ButtonGroup>
          )}
        />
      </Datagrid>
    </List>
  );
};

export default ContentPieceList;
